import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { SystemMetricsService, GpuMetrics } from '../../../../core/services/system-metrics.service';

interface Gauge {
  key: string;
  label: string;
  percent: number | null;
  detail: string;
}

const GB = 1024 ** 3;

/**
 * Compact CPU / RAM / GPU readout for the topbar.
 *
 * Hidden below `md` — the mobile topbar has no room for it, and the numbers are
 * a glanceable "is there headroom?" aid rather than something to act on.
 */
@Component({
  selector: 'app-system-monitor',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DecimalPipe],
  styles: [`
    :host { display: contents; }
    .sysmon { position: relative; cursor: default; }
    .sysmon .sysmon-tooltip {
      display: none;
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      min-width: 230px;
      background: #0d1117;
      border: 1px solid #1f2937;
      border-radius: 10px;
      padding: 10px 12px;
      font-size: 11px;
      color: #d1d5db;
      z-index: 60;
      box-shadow: 0 8px 30px rgba(0,0,0,0.6);
    }
    .sysmon:hover .sysmon-tooltip { display: block; }
    .meter { width: 100%; min-width: 34px; height: 3px; border-radius: 999px; background: #1f2937; overflow: hidden; }
    .meter > span { display: block; height: 100%; border-radius: 999px; transition: width 0.4s ease, background-color 0.4s ease; }
  `],
  template: `
    <div class="sysmon hidden md:flex items-center gap-3 px-2 py-1 rounded-lg hover:bg-[var(--color-surface-hover)] transition-colors"
         [class.opacity-40]="!metrics.reachable()">

      @for (g of gauges(); track g.key) {
        <div class="flex items-center gap-1.5">
          <span class="text-[10px] font-medium text-zinc-500">{{ g.label }}</span>
          <div class="flex flex-col gap-0.5">
            <span class="text-[11px] font-semibold tabular-nums leading-none whitespace-nowrap" [style.color]="color(g.percent)">
              {{ g.percent === null ? '—' : (g.percent | number: '1.0-0') + '%' }}
              @if (g.detail) {
                <span class="ml-1 text-[9px] font-normal text-zinc-500">{{ g.detail }}</span>
              }
            </span>
            <span class="meter">
              <span [style.width.%]="g.percent ?? 0" [style.background-color]="color(g.percent)"></span>
            </span>
          </div>
        </div>
      }

      <div class="sysmon-tooltip">
        @if (metrics.metrics(); as m) {
          <div class="font-semibold text-zinc-200 mb-2">Host resources</div>
          <div class="space-y-1.5">
            <div class="flex justify-between gap-4">
              <span class="text-zinc-500">CPU</span>
              <span class="text-zinc-200">
                {{ m.cpu.percent === null ? 'sampling…' : (m.cpu.percent | number: '1.0-1') + '%' }}
                <span class="text-zinc-500">· {{ m.cpu.cores }} cores</span>
              </span>
            </div>
            @if (m.cpu.load_avg; as la) {
              <div class="flex justify-between gap-4">
                <span class="text-zinc-500">Load avg</span>
                <span class="text-zinc-200 tabular-nums">
                  {{ la[0] | number: '1.2-2' }} · {{ la[1] | number: '1.2-2' }} · {{ la[2] | number: '1.2-2' }}
                </span>
              </div>
            }
            <div class="flex justify-between gap-4">
              <span class="text-zinc-500">RAM</span>
              <span class="text-zinc-200">
                {{ gb(m.memory.used_bytes) | number: '1.1-1' }} / {{ gb(m.memory.total_bytes) | number: '1.0-0' }} GB
              </span>
            </div>
            @for (gpu of m.gpus; track gpu.index) {
              <div class="pt-1.5 mt-1.5 border-t border-[#1f2937]">
                <div class="text-zinc-400 mb-1">GPU {{ gpu.index }} · {{ gpu.name }}</div>
                <div class="flex justify-between gap-4">
                  <span class="text-zinc-500">{{ gpu.utilization_percent != null ? 'Utilisation' : 'Utilisation (VRAM)' }}</span>
                  <span class="text-zinc-200">{{ gpuPercentLabel(gpu) }}</span>
                </div>
                @if (gpu.utilization_percent == null && gpu.utilization_memory_percent == null) {
                  <div class="text-[10px] text-zinc-500">SM utilisation not exposed by this driver</div>
                }
                <div class="flex justify-between gap-4">
                  <span class="text-zinc-500">VRAM</span>
                  <span class="text-zinc-200">
                    {{ gb(gpu.memory_used_bytes) | number: '1.1-1' }} / {{ gb(gpu.memory_total_bytes) | number: '1.1-1' }} GB
                  </span>
                </div>
                @if (gpu.temperature_c !== null) {
                  <div class="flex justify-between gap-4">
                    <span class="text-zinc-500">Temp</span>
                    <span class="text-zinc-200">{{ gpu.temperature_c }}°C</span>
                  </div>
                }
                @if (gpu.power_draw_w != null) {
                  <div class="flex justify-between gap-4">
                    <span class="text-zinc-500">Power</span>
                    <span class="text-zinc-200">{{ gpu.power_draw_w | number: '1.0-0' }} W</span>
                  </div>
                }
              </div>
            }
            @if (!m.gpus.length) {
              <div class="text-zinc-500 pt-1.5 mt-1.5 border-t border-[#1f2937]">No NVIDIA GPU detected</div>
            }
          </div>
          <div class="text-zinc-600 mt-2 text-[10px] leading-snug">
            Whole-host figures — this machine is shared with the sibling stacks.
            Refreshes every 5 s.
          </div>
        } @else if (!metrics.reachable()) {
          <div class="text-red-400">Metrics unavailable — API unreachable.</div>
        } @else {
          <div class="text-zinc-500">Sampling…</div>
        }
      </div>
    </div>
  `,
})
export class SystemMonitorComponent {
  readonly metrics = inject(SystemMetricsService);

  readonly gauges = computed<Gauge[]>(() => {
    const m = this.metrics.metrics();
    const gpu = m?.gpus[0];
    return [
      { key: 'cpu', label: 'CPU', percent: m?.cpu.percent ?? null, detail: '' },
      {
        key: 'ram',
        label: 'RAM',
        percent: m?.memory.percent ?? null,
        detail: m ? `${this.gb(m.memory.used_bytes).toFixed(1)}/${this.gb(m.memory.total_bytes).toFixed(0)}GB` : '',
      },
      {
        key: 'gpu',
        label: 'GPU',
        percent: this.gpuPercent(gpu),
        detail: gpu ? `${this.gb(gpu.memory_used_bytes).toFixed(1)}/${this.gb(gpu.memory_total_bytes).toFixed(1)}GB` : '',
      },
    ];
  });

  /** SM utilisation when the driver exposes it, else memory-controller %, else null. */
  gpuPercent(gpu: GpuMetrics | undefined): number | null {
    if (!gpu) return null;
    return gpu.utilization_percent ?? gpu.utilization_memory_percent ?? null;
  }

  gpuPercentLabel(gpu: GpuMetrics): string {
    if (gpu.utilization_percent != null) {
      return gpu.utilization_percent.toFixed(0) + '%';
    }
    if (gpu.utilization_memory_percent != null) {
      return gpu.utilization_memory_percent.toFixed(0) + '% VRAM';
    }
    return 'N/A';
  }

  gb(bytes: number): number {
    return bytes / GB;
  }

  /** Green under load, amber when tight, red when there is no headroom left. */
  color(percent: number | null): string {
    if (percent === null) return '#71717a';
    if (percent >= 85) return '#f87171';
    if (percent >= 60) return '#fbbf24';
    return '#4ade80';
  }
}
